import React, { useMemo } from 'react';
import { Spin, Button } from 'antd';
import { useAsync } from 'react-async-hook';
import { Banner } from 'components/ui/Banner';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import PageMeta from 'components/PageMeta/PageMeta';
import commonService from 'services/common.service';
import Card from 'components/Card/Card';
import { getFirstName, getFullName } from 'utils';

// Re-use existing instruction components from original flow
import MilitaryChildCareInstructions from './__components/MilitaryChildCareInstructions';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { ENDASH } from 'constants';
import { Launch } from '@carbon/icons-react';

export function FamilyApplicationSubmittedDeprecated({ application, household, setStep }) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const { loading, result: programs = [] } = useAsync(async () => {
    try {
      return await commonService.getPrograms();
    } catch (error) {
      newrelic.noticeError(error);
      return [];
    }
  }, []);

  const selectedProgramType = useMemo(
    () => programs.find((item) => item.id === application?.programType?.id)?.type,
    [application?.programType?.id, programs],
  );

  if (loading || !application) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Application Submitted" />

      <div className="p-7 pt-0">
        <Banner
          question="Your eligibility date is locked in"
          guidance={
            <>
              <div>
                Benefits will backdate to {dayjs(application?.submittedDate).format(DATE_FORMAT_SENTENCE)} once all
                documents are complete
              </div>

              <a
                href={`https://forms.office.com/Pages/ResponsePage.aspx?id=DLfJPWfkWkK24PKxgY3DCv9N5ZyIKKtPt6W_ebsggIpUQjYwWVdTN0g4TkZSTEdZNFNPR05NRjQ4WSQlQCN0PWcu&rd2eaa94831664c97bd4b42581980103f=${application?.id}`}
                target="_blank"
                rel="noreferrer"
                className="table mt-4"
              >
                <Button type="primary" className="inline-flex items-center gap-2">
                  <Launch className="mt-[1px]" />
                  Quick Feedback (2 minutes)
                </Button>
              </a>
            </>
          }
          className="mt-4 mb-12"
        />

        <Card title="Your family" className="mt-6" color="primary">
          <ul className="list-disc ml-5 block pb-2">
            <li>{getFullName(application?.applicant) || ENDASH} (Service Member)</li>
            <li>{getFullName(application?.additionalParents?.[0]) || ENDASH}</li>
            <li>
              Children:{' '}
              {application?.children
                ?.map((child) => getFirstName({ firstName: child.firstName }) || ENDASH)
                .join(', ') || ENDASH}
            </li>
          </ul>
        </Card>

        {selectedProgramType?.title === 'MCCYN' && (
          <MilitaryChildCareInstructions application={application} household={household} />
        )}

        <Card bordered={false} className="mt-16">
          <Button
            size="large"
            data-testid="back-button"
            onClick={() => {
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.SUBMITTED, application, stepConfig);

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            }}
            className="px-8"
          >
            Back
          </Button>
        </Card>
      </div>
    </>
  );
}
